<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue';
import {handleApiRequest} from '@/api/handleApiRequest';
import {getCanarySampleTimezone, updateCanarySampleTimezone} from '@/api/endpoints/patientWeb';
import {getNextActivationRoute, generateTimeOptions} from '@/services/piqSetupService';
import TheAbandonModal from '@/components/TheAbandonModal.vue';
import TheContactModal from '@/components/TheContactModal.vue';
import MultiViewLayout from '@/components/layouts/MultiViewLayout.vue';
import Svg from '@/components/Svg.vue';
import {baseStationSolid} from '@/assets/svgCanary';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {useQuasar} from 'quasar';

const defaultBedTime = '9:00 pm';
const {t} = useI18n();
const $q = useQuasar();
const router = useRouter();
const selectedHour = ref<string>('');
const bedTimeHours = ref(generateTimeOptions());

// computed
const dataCaptureMessage = computed(() => {
  return t('vue3_piq_bedtime_selection_popup', [`<strong>${showHourText.value}</strong>`]);
});

const showHourText = computed(() => {
  let selectedIndex = bedTimeHours.value.findIndex((d) => d['24H'] === selectedHour.value);
  // Data capture time = selected time + 4 hours
  if(selectedIndex === -1) { //tweak the default
    selectedIndex = 21;
  }
  const showHourIndex = (selectedIndex + 4) % bedTimeHours.value.length;

  return bedTimeHours.value[showHourIndex]?.text;
})

const formIsValid = computed(() => selectedHour.value !== '');

const focus = ref(false);

const convertedTimezone = ref('');

const showAbandon = ref(false);

const showContact = ref(false);

onMounted(async () => {
  $q.loading.show()
  const response = await handleApiRequest(() => getCanarySampleTimezone(), null, $q).finally(() => {
    $q.loading.hide()
  });
  const bedtime = response?.result?.bedtime;
  
  selectedHour.value = bedtime ? bedtime : '21:00';
});

// methods

async function onAbandonClick() {
  showAbandon.value = !showAbandon.value;
}

async function onInfoClick() {
  showContact.value = !showContact.value;
}

async function onNextClick() {
  $q.loading.show();

  await handleApiRequest(() =>
    updateCanarySampleTimezone(selectedHour.value), null, $q
  ).finally(() => {
    $q.loading.hide()
  });

  const nextRoute = await getNextActivationRoute($q);
  router.push({
    name: nextRoute
  })
    .catch(() => {
    });
}

watch(selectedHour, (nV, oV) => {
  let t: number = parseInt(nV.split(':')[0]);
  
  if (t >  12) {
    convertedTimezone.value = t - 12 + ':00 pm';
  } else if (t == 0) {
    convertedTimezone.value = '12:00 am';
  }else if (t == 12) {
    convertedTimezone.value = '12:00 pm';
  }
  else {
    convertedTimezone.value = t + ':00 am';
  }
});

const thirdLineVisible = ref(false);
function showThirdLine(){
  thirdLineVisible.value = true;
}
</script>

<template>
  <q-page padding class="canary setup timezone">
    <MultiViewLayout :onBackClick="onAbandonClick" back-icon-list="fa-solid fa-house-circle-xmark">

      <template v-slot:title>
        {{ t('hbs_flow_title') }}
      </template>

      <!-- Title -->
      <template #heading>
        {{ t('hbs_bedtime_title') }}
      </template>

      <template v-slot:auxiliary>
        <zbm-btn-icon @click="onInfoClick()" icon="fa-regular fa-question-circle" size="24px" text-color="dark-teal"/>
      </template>
      <!-- View Unique Content -->
      <template #default>

        <!-- Instructions -->
        <p>{{ t('hbs_bedtime_body') }}</p>

        <!-- Bedtime -->
        <div class="row flex-center">
          <div class="col">
            <div class="semibold q-pb-md"> {{ t('hbs_for_example') }}</div>
            <div class="row instruction_block justify-between">

              <div class="instruction_unit col">
                <q-icon name="fa-solid fa-bed"></q-icon>
                <div class="all-caps font-size-12 bold">{{ t('piq_bedtime_selection') }}</div>
                <div class="text-content-txt-secondary">{{ (selectedHour == '') ? defaultBedTime : convertedTimezone }}</div>
              </div>

              <div class="col q-px-sm arrow self-center">
                <div class="row justify-center">
                  <div class="col-10"><hr class="arrow body"/></div>
                  <div class="col-auto"> <div class="arrow head"></div></div>
                </div>
              </div>

              <div class="instruction_unit col">
                <q-icon name="fa-solid fa-clock"></q-icon>
                <div class="all-caps font-size-12 bold">{{ t('hbs_4_hours') }}</div>
              </div>


              <div class="col q-px-sm arrow self-center">
                <div class="row justify-center ">
                  <div class="col-10"><hr class="arrow body"/></div>
                  <div class="col-auto"> <div class="arrow head"></div></div>
                </div>
              </div>

              <div class="instruction_unit col">
                <div class="col q-mb-xs" id="bedtime">
                  <Svg :view-box="baseStationSolid.viewBox" :vector-paths="baseStationSolid.vectorPaths"/>
                </div>
                <div class="all-caps font-size-12 bold">{{ t('hbs_bedtime_transfer_label') }} </div>
                <div class="text-content-txt-secondary lower-case">{{ showHourText }}</div>
              </div>
            </div>
          </div>
        </div>


        <div class="column flex-center bedtime-select q-pb-xl">
          <div class="col q-pb-md label">{{ t('hbs_bedtime_input_label') }}</div> <!--My average bedtime is:-->
          <div class="row q-pb-md justify-center" >

            <div class="col-6">
              <q-input v-model="convertedTimezone"
                       input-class="text-center"
                       :placeholder="t('hbs_bedtime_placeholder')"
                       style="font-weight: 500; font-size: 72px;"
                       @click="focus= true">
              </q-input>
            </div>
            <q-dialog v-model="focus" @beforeHide="focus = false">
              <q-time v-model="selectedHour" :minute-options="[0]"
                      color="primitives-values-100">
                <template #default>
                  <zbm-btn v-close-popup
                           :color="$zb.enums.ButtonStdColorEnum.accent"
                           data-testid=""
                           :height="$zb.enums.ButtonHeightEnum.sm"
                           :label="t('acc_gen_continue')"
                           :width="$zb.enums.ButtonWidthEnum.wFull" />
                </template>
              </q-time>
            </q-dialog>
          </div>

          <div class="col call-to-action">
            <div>{{ t('survey_range_tap') }}</div> <!--Tap number to edit -->
          </div>

        </div>


        <!-- Selection Message -->
        <div v-if="formIsValid" class="selection-msg bg-primitives-foundation-10 text-content-txt-footer-alert p-8 radius-md">
          <p v-html="dataCaptureMessage"/>
          <p class="text-underline" @click="showThirdLine">{{ t('piq_bedtime_selection_question') }}</p>
          <p v-if="thirdLineVisible">{{ t('piq_bedtime_selection_upload_details') }}</p>
        </div>

        <!--Button-->
        <div class="col self-end" style="margin-top: 40px" v-if="selectedHour !== ''">
          <zbm-btn :color="$zb.enums.ButtonStdColorEnum.accent" :label="t('acc_gen_continue')" :outline="true"
                   :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg"
                   @click="onNextClick"/>
        </div>


      </template>

    </MultiViewLayout>

    <the-abandon-modal v-model="showAbandon"/>

    <the-contact-modal v-model="showContact"/>
  </q-page>
</template>
